import { Component, OnInit, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, inject } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { SHARED_IMPORTS } from '../shared/shared-imports';
import { ConfirmDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';
import { ContentService } from '../../services/content.service';
import { NewUnitTemplateComponent } from './new-unit-template/new-unit-template.component';



@Component({
	selector: 'app-manage-unit-templates',
	standalone: true,
	imports: [
		...SHARED_IMPORTS,
		MatDialogModule,
		MatSnackBarModule,
		MatButtonModule,
		MatIconModule,
		NewUnitTemplateComponent
	],
	templateUrl: 'components/manage-unit-templates/manage-unit-templates.component.html',
	schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class ManageUnitTemplatesComponent implements OnInit {
	
	private http = inject(HttpClient);
	private dialog = inject(MatDialog);
	private snack = inject(MatSnackBar);
	private content = inject(ContentService);

	constructor(
	) { }

	// --------- STATE ---------
	disableSaveNameButton = false;
	templates: any[] = [];
	unitTypes = { IVC: false, 'SC+Scantainer': false, IsoCage: false };
	readMode = false;
	unitType: 'IVC' | 'IsoCage' | 'SC+Scantainer' | 'SC%2BScantainer' = 'IVC';
	template: any = null;
	copiedTemplate: any = null;
	templateNotSaved = false;
	currentTemplateName = '';
	currentUser: any;

	private keys = Object.keys(this.unitTypes);

	// --------- BOUND METHODS FOR CHILD COMPONENTS ---------
	boundSortTemplates = this.sortTemplates.bind(this);
	boundAddOrUpdateTemplate = this.addOrUpdateTemplate.bind(this);
	boundTemplateSavedFeedback = this.templateSavedFeedback.bind(this);
	boundExitEditMode = this.exitEditMode.bind(this);
	boundGetTemplates = this.getTemplates.bind(this);
	boundEnsureTemplateShape = this.ensureTemplateShape.bind(this);

	// --------- LIFECYCLE ---------
	ngOnInit() {
		this.getCurrentUser();
		this.loadTemplates();
	}

	// --------- API CALLS ---------
	getCurrentUser(): void {
		this.http.get('/api/user/CurrentUser').subscribe((res: any) => {
			this.currentUser = res;
			// Keep old behavior
			this.readMode = this.currentUser?.GlobalAppRoleId?.localeCompare('00000000-0000-0000-0000-000000000002') === 1;
		});
	}

	private ensureTemplateShape(t: any) {
		return {
			...t,
			UnitConditionTemp: {
				TemperatureTarget: null,
				TemperatureAllowedDeviation: null,
				CageHumidityTarget: null,
				CageHumidityAllowedDeviation: null,
				AirChangeTarget: null,
				AirChangeAllowedDeviation: null,
				AirFlowDiffTarget: null,
				AirFlowDiffAllowedDeviation: null,
				IsPressurePositive: null,
				ExhaustAirFlowAllowedMinimum: null,
				PressurePaAllowedDeviation: null,
				PressurePaTarget: null,
				UpsPctBatteryAllowedMinimum: null,
				...(t.UnitConditionTemp || {}) // keep any API-provided values
			}
		};
	}

	loadTemplates(): void {
		const unitType = this.unitType === 'SC+Scantainer' ? 'SC%2BScantainer' : this.unitType;
		this.http
			.get<any[]>(`/api/UnitConditionTemplate/GetTemplates?unitType=${unitType}`)
			.subscribe((res) => {
				this.templates = (res || []).map(t => this.ensureTemplateShape(t));
				this.templates.forEach(t => (t.editMode = false));
				this.sortTemplates();
			});
	}

	getTemplates(): void {
		let unitType = this.unitType;
		if (unitType === 'SC+Scantainer') unitType = 'SC%2BScantainer';

		this.http.get<any[]>(`/api/UnitConditionTemplate/GetTemplates?unitType=${unitType}`)
			.subscribe((response: any) => {
				this.templates = ((response && response !== 'undefined') ? response : [])
					.map(t => this.ensureTemplateShape(t));
				this.templates.forEach(t => t.editMode = false);
				this.sortTemplates();
			});
	}

	// --------- UI HELPERS ---------
	sortTemplates(): void {
		this.templates.sort((a, b) => a.Name.toLowerCase().localeCompare(b.Name.toLowerCase()));
	}

	goBack(): void {
		window.history.back();
	}

	removeRedBorder(): void {
		const els = document.getElementsByClassName('redBorder');
		for (let i = els.length - 1; i >= 0; i--) {
			els[els.length - 1].classList.remove('redBorder');
		}
	}

	templateSavedFeedback(template: any): void {
		const feedbackMsg =
			template.Id == null
				? this.content.getContent('ManageUnitTemplates.NewTemplateHasBeenAdded')
				: this.content.getContent('ManageUnitTemplates.TemplateHasBeenUpdated');

		if (template.Message === '') {
			this.removeRedBorder();
			this.snack.open(feedbackMsg, '', { duration: 3000, horizontalPosition: 'center', verticalPosition: 'bottom' });
		}
	}

	// --------- ACTIONS ---------
	selectUnitType(id: number): void {
		const targetType = this.keys[id] as typeof this.unitType;

		if (this.template !== null && !this.template.checkIfNull) {
			const ref = this.dialog.open(ConfirmDialogComponent, {
				data: {
					title: this.content.getContent('ManageUnitTemplates.SaveTemplate'),
					okText: this.content.getContent('Standard.Save'),
					cancelText: this.content.getContent('Standard.Cancel')
				}
			});

			ref.afterClosed().subscribe((ok) => {
				if (ok) {
					this.addOrUpdateTemplate(this.template, targetType);
				} else {
					this.exitEditMode(this.template);
					this.unitType = targetType;
					this.getTemplates();
				}
			});

		} else if (this.template !== null && this.template.checkIfNull) {
			this.exitEditMode(this.template);
			this.unitType = targetType;
			this.getTemplates();

		} else {
			this.unitType = targetType;
			this.getTemplates();
		}
	}

	addNewEmptyTemplate(): void {
		let counter = 0;
		for (const t of this.templates) {
			const name = t?.Name;
			if (name && name.includes(this.content.getContent('ManageUnitTemplates.AddTemplateName'))) counter++;
		}
		counter++;

		this.templateNotSaved = false;

		this.template = {
			Id: null,
			Name: `${this.content.getContent('ManageUnitTemplates.AddTemplateName')} ${counter}`,
			Description: null,
			FirstApplicationTimestamp: null,
			UsedOn: 0,
			Message: '',
			UnitType: '',
			UnitConditionTemp: {
				TemperatureTarget: null,
				TemperatureAllowedDeviation: null,
				CageHumidityTarget: null,
				CageHumidityAllowedDeviation: null,
				AirChangeTarget: null,
				AirChangeAllowedDeviation: null,
				AirFlowDiffTarget: null,
				AirFlowDiffAllowedDeviation: null,
				IsPressurePositive: null,
				ExhaustAirFlowAllowedMinimum: null,
				PressurePaAllowedDeviation: null,
				PressurePaTarget: null,
				UpsPctBatteryAllowedMinimum: null
			},
			editMode: true,
			titleEditMode: false,
			checkIfNull: true
		};
	}

	addNewTemplate(): void {
		window.scrollTo({ top: 0, behavior: 'smooth' });
		this.addNewEmptyTemplate();
		this.removeRedBorder();
	}

	addOrUpdateTemplate(template: any, selectedUnitType: string | null): any {
		template.UnitType = this.unitType;

		const tempTarget = template.UnitConditionTemp?.TemperatureTarget;
		if (isNaN(tempTarget) && typeof tempTarget === 'string' && tempTarget.includes(',')) {
			template.UnitConditionTemp.TemperatureTarget = tempTarget.replace(',', '.');
		}

		this.http.post<any>('/api/UnitConditionTemplate/AddOrUpdateUnitConditionTemplate', template)
			.subscribe((response) => {
				const elementPos = this.templates.indexOf(template);
				this.templateNotSaved = false;

				template.Message = response?.Message;
				this.templateSavedFeedback(template);

				// add (new)
				if (template.Id == null && !template.copy && template.Message === '') {
					//this.templates.push(template);
					if (selectedUnitType !== null) this.unitType = selectedUnitType as any;
				}

				template.Id = response?.Id;

				if (template.Id != null && template.Message === '') {
					template.editMode = false;
					this.templates.push(template);
					this.sortTemplates();
					if (selectedUnitType !== null) this.unitType = selectedUnitType as any;
					this.template = null;
					return;
				}

				// error/validation path:
				const tempTemplate = response;
				if (template.Id == null && template.Message !== '') {
					const scopeTemplate = this.template !== null ? this.template : this.copiedTemplate;

					if (scopeTemplate) {
						this.removeRedBorder();

						if (tempTemplate?.Name !== scopeTemplate?.Name) {
							const el = document.getElementsByClassName('templateTitleInput unitTemplateInputBorder');
							if (el.length) el[0].classList.add('redBorder');
						}

						const keyNames = Object.keys(scopeTemplate?.UnitConditionTemp || {});
						for (const key of keyNames) {
							if (tempTemplate?.UnitConditionTemp?.[key] != scopeTemplate.UnitConditionTemp[key]) {
								const el = document.getElementsByClassName('unitTemplateInputBorder ' + key);
								if (el.length) el[0].classList.add('redBorder');
							}
						}

						scopeTemplate.editMode = true;
						scopeTemplate.Message = this.content.getContent('ManageUnitTemplates.TemplateNotSaved') + '. \n' + template.Message;
					}

				} else if (template.Id != null && elementPos >= 0 && this.templates[elementPos]) {
					this.templates[elementPos].Message =
						this.content.getContent('ManageUnitTemplates.TemplateNotUpdated') + '. \n' + template.Message;
					this.templates[elementPos].editMode = true;

					if (tempTemplate?.Name !== this.templates[elementPos].Name) {
						const el = document.getElementsByClassName('templateTitleInput unitTemplateInputBorder');
						if (el.length) el[0].classList.add('redBorder');
					}

					const keyNames = Object.keys(this.templates[elementPos].UnitConditionTemp || {});
					for (const key of keyNames) {
						if (tempTemplate?.UnitConditionTemp?.[key] != this.templates[elementPos].UnitConditionTemp[key]) {
							const el = document.getElementsByClassName(key);
							if (el.length > elementPos + 1) el[elementPos + 1].classList.add('redBorder');
						}
					}
				}

				this.templateNotSaved = true;
			});

		return template;
	}

	exitEditMode(template: any): void {
		template.editMode = false;

		if (template.Id == null) {
			this.template = null;
		} else {
			const pos = this.templates.indexOf(template);
			if (pos >= 0) {
				this.templates[pos].Message = '';
				this.templates[pos].UnitConditionTemp = this.templates[pos].OldTemplate;
				this.templates[pos].Name = this.templates[pos].OldName;
			}
		}

		if (template.copy) {
			const index = this.templates.indexOf(template);
			if (index >= 0) this.templates.splice(index, 1);
			template = null;
			if (this.copiedTemplate) this.copiedTemplate.Message = '';
		}

		this.templateNotSaved = false;
		this.removeRedBorder();
	}
}
